import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  ArrowTrendingUpIcon,
  BookOpenIcon,
  DocumentIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
  SparklesIcon,
  Square3Stack3DIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { buildPublicUrl } from '@/utils/public-url';
import { Content } from '@/lib/types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (item: Content) => void | Promise<void>;
};

type IconChoice = {
  icon: typeof ViewfinderCircleIcon;
  bg: string;
  color: string;
};

function pickIcon(name: string | undefined): IconChoice {
  const n = (name || '').toLowerCase();
  if (n.includes('drill')) return { icon: ViewfinderCircleIcon, bg: 'red.100', color: 'red.700' };
  if (n.includes('skill')) return { icon: SparklesIcon, bg: 'green.100', color: 'green.700' };
  if (n.includes('play')) return { icon: ArrowTrendingUpIcon, bg: 'blue.100', color: 'blue.700' };
  if (n.includes('tactic'))
    return { icon: Square3Stack3DIcon, bg: 'purple.100', color: 'purple.700' };
  if (n.includes('document')) return { icon: DocumentIcon, bg: 'orange.100', color: 'orange.700' };
  if (n.includes('article') || n.includes('post'))
    return { icon: BookOpenIcon, bg: 'cyan.100', color: 'cyan.700' };
  return { icon: DocumentIcon, bg: 'gray.100', color: 'gray.700' };
}

async function ensurePublicLink(item: Content): Promise<string | null> {
  let fallback: string | null = item.public_link || null;
  if (!item.public_link) {
    try {
      const res = await api.put(`contents/public/${item.id}`);
      fallback = res.data?.data?.public_link || null;
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to generate public link',
      });
      return null;
    }
  }
  if (item.reference_id) return buildPublicUrl('content', item.reference_id);
  return fallback;
}

export default function ShareContentDialog({ isOpen, onClose, onPrint }: Props) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Content[]>([]);
  const [recent, setRecent] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setDebouncedQuery('');
    setResults([]);
    if (recent.length === 0 && workspaceId) {
      api
        .get(`contents/recent/${workspaceId}?limit=8`)
        .then((res) => setRecent(res.data.data || []))
        .catch(() => undefined);
    }
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, workspaceId, recent.length]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    const q = debouncedQuery;
    if (!q) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    api
      .get(`contents/search/${workspaceId}?q=${encodeURIComponent(q)}&limit=20`)
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, workspaceId, isOpen]);

  const showRecent = !debouncedQuery;
  const items = useMemo(
    () => (showRecent ? recent : results),
    [showRecent, recent, results],
  );

  const handleCopy = async (item: Content) => {
    setBusyId(item.id);
    const link = await ensurePublicLink(item);
    setBusyId(null);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toaster.create({ type: 'success', title: 'Public link copied to clipboard' });
    } catch {
      toaster.create({ type: 'error', title: 'Could not copy link to clipboard' });
    }
  };

  const handlePrint = (item: Content) => {
    onClose();
    void onPrint(item);
  };

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <DialogBackdrop />
      <DialogContent maxW='720px'>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Share content</DialogTitle>
          <Text fontSize='sm' color='gray.500' mt={1}>
            Find what you want to share, then copy a link or print.
          </Text>
        </DialogHeader>
        <DialogBody pb={5}>
          <InputGroup
            startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} color='gray.400' />}
          >
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search what you want to share...'
              bg='gray.50'
              borderColor='gray.200'
              borderRadius='lg'
              size='lg'
              fontSize='md'
            />
          </InputGroup>

          <Box mt={4}>
            <Text
              fontSize='xs'
              fontWeight='bold'
              letterSpacing='wider'
              color='gray.500'
              mb={2}
            >
              {showRecent ? 'RECENT — PICK SOMETHING TO SHARE' : 'RESULTS'}
            </Text>

            {isLoading && (
              <Flex justify='center' py={6}>
                <Spinner size='sm' color='gray.400' />
              </Flex>
            )}

            {!isLoading && items.length === 0 && (
              <Box py={6} textAlign='center'>
                <Text fontSize='sm' color='gray.500'>
                  {showRecent
                    ? 'No content yet — start by creating one.'
                    : `No matches for "${debouncedQuery}".`}
                </Text>
              </Box>
            )}

            {!isLoading && items.length > 0 && (
              <Stack gap={1}>
                {items.map((item) => {
                  const choice = pickIcon(item.content_type_name);
                  const isBusy = busyId === item.id;
                  return (
                    <Flex
                      key={item.id}
                      align='center'
                      gap={3}
                      px={2}
                      py={2}
                      borderRadius='md'
                      width='100%'
                    >
                      <Flex
                        align='center'
                        justify='center'
                        w={9}
                        h={9}
                        borderRadius='md'
                        bg={choice.bg}
                        flexShrink={0}
                      >
                        <Icon as={choice.icon} w={4} h={4} color={choice.color} />
                      </Flex>
                      <Box flex='1' minW={0}>
                        <Text
                          fontSize='sm'
                          fontWeight='semibold'
                          color='gray.900'
                          lineClamp={1}
                        >
                          {item.name || 'Untitled'}
                        </Text>
                        <Text fontSize='xs' color='gray.500' lineClamp={1}>
                          {item.content_type_name || 'Content'}
                          {item.created_user && ` · ${item.created_user}`}
                        </Text>
                      </Box>
                      <Flex gap={2} flexShrink={0}>
                        <Button
                          size='sm'
                          variant='outline'
                          borderRadius='md'
                          loading={isBusy}
                          onClick={() => handleCopy(item)}
                        >
                          <Icon as={LinkIcon} w={4} h={4} />
                          Copy link
                        </Button>
                        <Button
                          size='sm'
                          variant='outline'
                          borderRadius='md'
                          loading={isBusy}
                          onClick={() => handlePrint(item)}
                        >
                          <Icon as={PrinterIcon} w={4} h={4} />
                          Print
                        </Button>
                      </Flex>
                    </Flex>
                  );
                })}
              </Stack>
            )}
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
